import { useMutation } from "@apollo/client";
import { create, show, useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { useLocation } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import useTypedContext from "hooks/useTypedContext";
import Box from "ds/components/Box";
import Tile from "ds/components/Tile";

import { REFETCH_QUERIES_LIST } from "./constants";
import { DELETE_DEPLOYMENT } from "./gql";

type DeleteConfirmationProps = {
  name: string;
  id: string;
  templateID: string;
  onDelete?: () => void;
};

const DeleteConfirmation = create(function DeleteConfirmation({
  name,
  id,
  templateID,
  onDelete,
}: DeleteConfirmationProps) {
  const { pathname } = useLocation();
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const [skipStackDeletion, setSkipStackDeletion] = useState(false);
  const isStackDeletionOptionEnabled = !pathname.includes("/templates/template/");

  const modal = useModal();

  const [deleteDeployment, { loading, data }] = useMutation(DELETE_DEPLOYMENT, {
    refetchQueries: REFETCH_QUERIES_LIST,
    awaitRefetchQueries: true,
    variables: { templateID, id, skipStackDeletion },
  });

  const handleDeleteDeployment = () => {
    deleteDeployment()
      .then(() => {
        reportSuccess({
          message: `Deployment "${name}" has been queued for deletion`,
        });
        modal.resolve();
        modal.hide();
        onDelete?.();
      })
      .catch(onError);
  };

  return (
    <DeleteConfirmationModal
      title="Delete deployment"
      onConfirm={handleDeleteDeployment}
      isLoading={loading}
      isDismissable={!loading && !data?.templateDeploymentDelete?.id}
      size={isStackDeletionOptionEnabled ? "large" : "default"}
    >
      <ConfirmationModalMessage>
        Are you sure you want to delete deployment <strong>{name}</strong>? This action cannot be
        undone.
      </ConfirmationModalMessage>
      {isStackDeletionOptionEnabled && (
        <Box gap="x-large" margin="large 0 0 0">
          <Tile
            title="Delete stacks"
            indicator="radio"
            description="All stacks that belong to this deployment will be removed with it."
            selected={!skipStackDeletion}
            onClick={() => setSkipStackDeletion(false)}
            disabled={loading}
          />
          <Tile
            title="Keep stacks"
            indicator="radio"
            description="Stacks that belong to this deployment will remain available when the deployment is removed."
            selected={skipStackDeletion}
            onClick={() => setSkipStackDeletion(true)}
            disabled={loading}
          />
        </Box>
      )}
    </DeleteConfirmationModal>
  );
});

export const showDeleteConfirmation = (props: DeleteConfirmationProps) =>
  show(DeleteConfirmation, props);
